/**
 * Entity Role/Class Definitions
 * Shared between server and client for role-based access control
 *
 * TWO CLASSES: staff, and everyone else. `acctEntity.class` answers exactly one
 * question — is this a Mach Five platform administrator? Everything else about what
 * a person may do comes from their ORG membership (acctGroup.class × member_role,
 * see tenancy.js's CAPABILITY_MATRIX), not from this ladder.
 *
 * COLLAPSED 2026-08-13 (migration 025) from nine values to two:
 *   admin_root/business/support  → admin_root_role
 *   client_tier_1..4 + standard  → client_standard_role
 *
 * Neither collapse changed a single permission, which is the point. The three admin
 * tiers already resolved to an identical capability set — resolveCapabilities never
 * distinguished them — and the four paid client tiers were audited and found to have
 * NO functional effect anywhere: not on capabilities, quotas, billing (coreInvoice
 * has no route and no query), feature gating, or provisioning. They were labels that
 * looked like permissions, which is worse than no labels: every reader had to
 * rediscover that a "Tier 3" account can do precisely what a free one can.
 *
 * If a plan/billing tier is wanted later, it belongs in its own column or table where
 * it can mean something, NOT smuggled back into the permission ladder.
 *
 * Values stay NUMERIC (100 / 0) rather than becoming a boolean: isAdminRole's >= 50
 * threshold, canAssignRole's comparison, coreAssets and adminSession all key off the
 * number, and swapping in a boolean is a larger, riskier refactor for no gain.
 *
 * The dormant read-only machinery from the same day's authz work still stands
 * (scope.adminCaps, isReadOnlyAdminClass): it is derived from CAPABILITY_MATRIX, not
 * from any name here, so it begins enforcing on its own if a restricted admin class
 * is ever added. See the dormancy note in tenancy.js and test-admin-authority-split.
 */

export const EntityClasses = Object.freeze({
  admin_root_role: {
    value: 100,
    label: "Administrator",
    desc: "Mach Five platform staff — full access across every organization"
  },
  client_standard_role: {
    value: 0,
    label: "Client",
    desc: "Everyone else — what they can do comes from their organization membership, not this class"
  }
});

/**
 * Get role by name
 */
export function getRoleByName(name) {
  return EntityClasses[name] || null;
}

/**
 * Get role by value
 */
export function getRoleByValue(value) {
  return Object.entries(EntityClasses).find(([_, role]) => role.value === value)?.[1] || null;
}

/**
 * Check if user can assign target role
 */
export function canAssignRole(currentUserRoleValue, targetRoleValue) {
  return targetRoleValue <= currentUserRoleValue;
}

/**
 * Get roles sorted by privilege (highest first)
 */
export function getRolesSorted() {
  return Object.entries(EntityClasses)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Get roles user can assign (their level or lower)
 */
export function getAssignableRoles(currentUserRoleValue) {
  return getRolesSorted().filter(role => role.value <= currentUserRoleValue);
}

/**
 * Check if role is admin level (>= 50)
 */
export function isAdminRole(roleValue) {
  return roleValue >= 50;
}

/**
 * Check if role is client level (< 50)
 */
export function isClientRole(roleValue) {
  return roleValue < 50;
}
